/**
 * Sticky add-to-cart bar (2026-09-09)
 *
 * Drives the REAL product-page controls rather than re-implementing
 * variant matching, money formatting, or cart logic:
 *  - Changing a sticky variant <select> clicks/updates the matching real
 *    [data-variant-input] element (radio or per-option <select>) rendered
 *    by variant-button.liquid / variant-dropdown.liquid, then lets the
 *    theme's own variant-change handling do its thing.
 *  - The sticky bar's price / button label / disabled state / colors are
 *    copied from the real [data-product-price] / [data-add-to-cart]
 *    elements on every `variant:change` event the theme already
 *    dispatches. Colors are copied via getComputedStyle() rather than a
 *    matching CSS class, because the real button's red comes from a
 *    ".btn.add-to-cart" rule in theme.css whose effect turned out to be
 *    dependent on the button's ancestor/DOM position; reading the live
 *    computed style sidesteps that and stays correct if the theme's
 *    color ever changes.
 *  - "Add to cart" syncs quantity into the real form (creating a hidden
 *    quantity input if the template has no quantity_selector block) and
 *    clicks the real [data-add-to-cart] button, so AJAX cart / cart
 *    drawer / dynamic checkout / analytics all keep working unchanged.
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function clampQty(value) {
    var n = parseInt(value, 10);
    if (isNaN(n) || n < 1) n = 1;
    return n;
  }

  function initStickyBar(bar) {
    var scope = bar.closest('[data-section-type="product"]') || document;

    // Re-queried on every sync/click rather than cached once, because the
    // theme (or an app embed) can re-render parts of the product form after
    // page load -- e.g. after a successful add, or a history-state variant
    // swap -- which replaces these nodes. A stale cached reference would
    // silently freeze the sticky bar's price/button state at whatever it
    // was the moment of replacement, and a click on a detached button
    // wouldn't reach the live form at all. `scope` (the section wrapper)
    // is far more stable than the controls inside it, so re-querying from
    // it is cheap insurance against exactly that class of bug.
    function getRealAddBtn() { return scope.querySelector('[data-add-to-cart]'); }
    function getRealPriceEl() { return scope.querySelector('[data-product-price]'); }
    function getRealComparePriceEl() { return scope.querySelector('[data-compare-price]'); }
    function getRealCompareWrap() { return scope.querySelector('[data-product-price-wrap]'); }
    function getRealQtyInput() { return scope.querySelector('input[name="quantity"]'); }

    var realAddBtn = getRealAddBtn();
    if (!realAddBtn) return;

    var stickyPriceEl = bar.querySelector('[data-sticky-price]');
    var stickyComparePriceEl = bar.querySelector('[data-sticky-compare-price]');
    var stickyBtn = bar.querySelector('[data-sticky-add-to-cart]');
    var stickyBtnText = bar.querySelector('[data-sticky-add-to-cart-text]');
    var qtyInput = bar.querySelector('[data-sticky-qty-input]');
    var qtyMinus = bar.querySelector('[data-sticky-qty-minus]');
    var qtyPlus = bar.querySelector('[data-sticky-qty-plus]');
    var optionSelects = Array.prototype.slice.call(bar.querySelectorAll('[data-sticky-option-select]'));

    var injectedQtyInput = null;
    function ensureRealQtyInput() {
      var realQtyInput = getRealQtyInput();
      if (realQtyInput) return realQtyInput;
      if (injectedQtyInput && injectedQtyInput.isConnected) return injectedQtyInput;
      injectedQtyInput = document.createElement('input');
      injectedQtyInput.type = 'hidden';
      injectedQtyInput.name = 'quantity';
      injectedQtyInput.setAttribute('form', bar.getAttribute('data-form-id'));
      bar.appendChild(injectedQtyInput);
      return injectedQtyInput;
    }

    function syncRealQty() {
      ensureRealQtyInput().value = qtyInput ? clampQty(qtyInput.value) : 1;
    }

    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        qtyInput.value = clampQty(qtyInput.value);
        syncRealQty();
      });
    }
    if (qtyMinus) {
      qtyMinus.addEventListener('click', function () {
        qtyInput.value = Math.max(1, clampQty(qtyInput.value) - 1);
        syncRealQty();
      });
    }
    if (qtyPlus) {
      qtyPlus.addEventListener('click', function () {
        qtyInput.value = clampQty(qtyInput.value) + 1;
        syncRealQty();
      });
    }

    // Changing a sticky variant select drives the real per-option control.
    optionSelects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        var idx = sel.getAttribute('data-option-index');
        var val = sel.value;
        var realInputs = scope.querySelectorAll('[data-variant-input][data-index="' + idx + '"]');
        Array.prototype.forEach.call(realInputs, function (input) {
          if (input.tagName === 'SELECT') {
            if (input.value !== val) {
              input.value = val;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (input.type === 'radio' && input.value === val && !input.checked) {
            input.click();
          }
        });
      });
    });

    // Keep the sticky selects in sync if the visitor uses the real picker
    // (e.g. after scrolling back up).
    function syncSelectsFromDom() {
      optionSelects.forEach(function (sel) {
        var idx = sel.getAttribute('data-option-index');
        var realInputs = scope.querySelectorAll('[data-variant-input][data-index="' + idx + '"]');
        var current = null;
        Array.prototype.forEach.call(realInputs, function (input) {
          if (input.tagName === 'SELECT') current = input.value;
          else if (input.type === 'radio' && input.checked) current = input.value;
        });
        if (current !== null && sel.value !== current) sel.value = current;
      });
    }

    function syncButtonFromReal() {
      var btn = getRealAddBtn();
      if (!btn) return;
      if (stickyBtnText) {
        var realText = btn.querySelector('[data-add-to-cart-text]');
        stickyBtnText.textContent = (realText ? realText.textContent : btn.textContent).trim();
      }
      if (stickyBtn) {
        stickyBtn.disabled = !!btn.disabled;
        // Match the real button's live look (including its disabled
        // gray-border state) instead of hardcoding a color in CSS -- see
        // the file header comment for why.
        var realStyle = getComputedStyle(btn);
        stickyBtn.style.backgroundColor = realStyle.backgroundColor;
        stickyBtn.style.color = realStyle.color;
        stickyBtn.style.borderColor = realStyle.borderColor;
      }
    }

    function syncPriceFromReal() {
      var priceEl = getRealPriceEl();
      if (stickyPriceEl && priceEl) stickyPriceEl.textContent = priceEl.textContent.trim();
      if (stickyComparePriceEl) {
        var compareWrap = getRealCompareWrap();
        var comparePriceEl = getRealComparePriceEl();
        var showCompare =
          compareWrap &&
          !compareWrap.classList.contains('hide') &&
          comparePriceEl &&
          comparePriceEl.textContent.trim();
        if (showCompare) {
          stickyComparePriceEl.textContent = comparePriceEl.textContent.trim();
          stickyComparePriceEl.hidden = false;
        } else {
          stickyComparePriceEl.hidden = true;
        }
      }
    }

    function syncAllFromReal() {
      syncButtonFromReal();
      syncPriceFromReal();
      syncSelectsFromDom();
    }

    document.addEventListener('variant:change', function () {
      // Let the theme's own variant:change handler (already registered
      // before this deferred script runs) finish updating the real price
      // / button / sold-out state first. A single requestAnimationFrame
      // covers the common case; the extra setTimeout is cheap insurance
      // against a slower or doubly-deferred re-render still landing after
      // the first paint (seen intermittently after cart-drawer / dynamic
      // checkout re-renders).
      window.requestAnimationFrame(syncAllFromReal);
      setTimeout(syncAllFromReal, 300);
    });

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        if (stickyBtn.disabled) return;
        var btn = getRealAddBtn();
        if (!btn) return;
        syncRealQty();
        stickyBtn.disabled = true;
        var loadingText = stickyBtn.getAttribute('data-loading-text') || 'Adding...';
        if (stickyBtnText) stickyBtnText.textContent = loadingText;

        var resetDone = false;
        var reset = function () {
          if (resetDone) return;
          resetDone = true;
          document.removeEventListener('ajaxProduct:added', reset);
          document.removeEventListener('cart:updated', reset);
          syncButtonFromReal();
        };
        document.addEventListener('ajaxProduct:added', reset);
        document.addEventListener('cart:updated', reset);
        // Fallback in case neither AJAX event fires (e.g. cart type is a
        // full-page redirect rather than a drawer/notification).
        setTimeout(reset, 4000);

        btn.click();
      });
    }

    // Show the bar once the real Add to Cart button has scrolled out of
    // view above the viewport; hide it again once it (or the footer)
    // comes back into view.
    var footer = document.querySelector('footer, .site-footer, [data-section-type="footer"]');
    var buttonAboveViewport = false;
    var footerVisible = false;

    function updateVisibility() {
      var visible = buttonAboveViewport && !footerVisible;
      bar.classList.toggle('sticky-atc--visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    var buttonObserver = null;
    var observedBtn = null;
    function ensureButtonObserved() {
      if (!('IntersectionObserver' in window)) return;
      var btn = getRealAddBtn();
      if (!btn || btn === observedBtn) return;
      if (buttonObserver) buttonObserver.disconnect();
      observedBtn = btn;
      buttonObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          buttonAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        });
        updateVisibility();
      }, { threshold: 0 });
      buttonObserver.observe(btn);
    }

    if ('IntersectionObserver' in window) {
      ensureButtonObserved();

      if (footer) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            footerVisible = entry.isIntersecting;
          });
          updateVisibility();
        }, { threshold: 0, rootMargin: '0px 0px -10% 0px' }).observe(footer);
      }
    } else {
      bar.classList.add('sticky-atc--visible');
    }

    // Re-check the observed button on every sync pass too, in case the
    // theme swapped it out for a new node (see the comment on
    // getRealAddBtn above) -- otherwise the visibility observer would keep
    // watching a detached element and the bar could get stuck shown/hidden.
    var syncAllFromRealBase = syncAllFromReal;
    syncAllFromReal = function () {
      syncAllFromRealBase();
      ensureButtonObserved();
    };

    // Initial sync (in case the section rendered with a non-default
    // current_variant, e.g. from a ?variant= URL param).
    syncButtonFromReal();
    syncPriceFromReal();
  }

  ready(function () {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sticky-atc]'), initStickyBar);
  });
})();
